<template>
    <component :is="page ? 'div' : 'a-modal'" v-bind="page ? {} : modalProps" @cancel="$emit('close')">
        <div class="agm-modal" :class="{ 'agm-page': page }">
            <div class="agm-head" v-if="!page">
                <div>
                    <h3>Adicionar a um grupo</h3>
                    <div class="agm-sub">{{ subtitle }}</div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="agm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <div class="agm-tools">
                <a-input v-model="search" class="agm-search" placeholder="Buscar grupo" allow-clear>
                    <a-icon slot="prefix" type="search" />
                </a-input>
            </div>

            <div class="agm-body">
                <a-table
                    size="small"
                    :columns="columns"
                    :data-source="filteredGroups"
                    row-key="id"
                    :loading="loading"
                    :pagination="false"
                    :customRow="groupRow"
                    :locale="{ emptyText: 'Nenhum grupo cadastrado.' }"
                >
                    <template slot="check" slot-scope="text, group">
                        <a-checkbox :checked="isSelected(group)" @click.native.stop @change="toggle(group)" />
                    </template>
                    <template slot="nome" slot-scope="text, group">
                        <span class="agm-strong">{{ group.descricao }}</span>
                        <a-tag v-if="group.produto && !group.remove" color="green" class="agm-tag">No grupo</a-tag>
                        <a-tag v-else-if="group.produto && group.remove" color="red" class="agm-tag">Será removido</a-tag>
                    </template>
                </a-table>
            </div>

            <div class="agm-foot">
                <a-button v-if="!page" @click="$emit('close')">Cancelar</a-button>
                <a-button type="primary" :loading="saving" @click="save">Salvar</a-button>
            </div>
        </div>
    </component>
</template>

<script>
import ProductGroupService from "@/services/v3/management/ProductGroupService.js";
import ErrorHandlerService from "@/services/ErrorHandlerService";

export default {
    name: "AddToGroupV2Modal",
    props: {
        product: { type: [Object, Array], required: true },
        page: { type: Boolean, default: false },
    },
    data() {
        return {
            service: new ProductGroupService(),
            loading: false,
            saving: false,
            groups: [],
            search: "",
            columns: [
                { title: "", key: "check", width: 50, align: "center", scopedSlots: { customRender: "check" } },
                { title: "Grupo", key: "nome", scopedSlots: { customRender: "nome" } },
                { title: "Produtos", dataIndex: "count", key: "count", align: "center", width: 100 },
            ],
        };
    },
    computed: {
        modalProps() {
            return {
                visible: true,
                width: "40vw",
                footer: null,
                closable: false,
                destroyOnClose: true,
                centered: true,
                wrapClassName: "agm-wrap",
            };
        },
        products() {
            return Array.isArray(this.product) ? this.product : [this.product];
        },
        subtitle() {
            if (this.products.length === 1) return this.products[0].pro_descricao;
            return `${this.products.length} produtos selecionados`;
        },
        filteredGroups() {
            const term = (this.search || "").trim().toLowerCase();
            if (!term) return this.groups;
            return this.groups.filter((g) => String(g.descricao || "").toLowerCase().includes(term));
        },
    },
    created() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            const productId = Array.isArray(this.product) ? undefined : this.product.pro_id;
            this.service
                .loadProductGroups(productId)
                .then(({ data }) => {
                    this.groups = (Array.isArray(data) ? data : []).map((g) => ({ ...g, checked: false, remove: false }));
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        isSelected(group) {
            return group.produto ? !group.remove : group.checked;
        },
        toggle(group) {
            if (group.produto) group.remove = !group.remove;
            else group.checked = !group.checked;
        },
        groupRow(group) {
            return { on: { click: () => this.toggle(group) } };
        },
        async save() {
            this.saving = true;
            try {
                const toAdd = this.groups.filter((g) => g.checked);
                const toRemove = this.groups.filter((g) => g.remove);
                for (const group of toAdd) {
                    for (const product of this.products) {
                        await this.service.addProduct(product.pro_id, group.id);
                    }
                }
                for (const group of toRemove) {
                    await this.service.deleteProduct(group.produto.id);
                }
                this.$emit("updated");
                if (this.page) {
                    this.$store.dispatch("notification/add", { type: "success", message: "Grupos atualizados com sucesso!" });
                    this.load();
                } else {
                    this.$emit("close");
                }
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.agm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.agm-page {
        max-height: none;
        overflow: visible;

        .agm-tools {
            padding-left: 0;
            padding-right: 0;
        }

        .agm-body {
            overflow: visible;
            padding: 0;
            border-top: none;
        }

        .agm-foot {
            padding: 14px 0 0;
            border-top: none;
        }
    }
}
.agm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.agm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.agm-x.ant-btn,
.agm-x.ant-btn:focus,
.agm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.agm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.agm-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.agm-search {
    flex: 1 1 240px;
    max-width: 360px;
}
.agm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.agm-body ::v-deep .ant-table-tbody > tr {
    cursor: pointer;
}
.agm-strong {
    font-weight: 600;
    color: #262626;
}
.agm-tag {
    margin-left: 8px;
}
.agm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.agm-wrap {
    .ant-modal {
        min-width: 480px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
